import { useEffect } from 'react';
import { useViewer } from '../store/context';
import type { ThemePref } from '../store/viewer-store';

const STORAGE_KEY = 'markroom.theme';

/** Read the persisted theme preference (universal localStorage; safe default). */
export function loadThemePref(): ThemePref {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    // ignore storage failures
  }
  return 'system';
}

function systemPrefersDark(): boolean {
  return (
    typeof matchMedia !== 'undefined' && matchMedia('(prefers-color-scheme: dark)').matches
  );
}

/** Apply the resolved theme to <html> and keep it in sync with system + pref. */
export function useTheme(): void {
  const themePref = useViewer((s) => s.themePref);

  useEffect(() => {
    const apply = () => {
      const dark = themePref === 'dark' || (themePref === 'system' && systemPrefersDark());
      const root = document.documentElement;
      root.classList.toggle('dark', dark);
      root.style.colorScheme = dark ? 'dark' : 'light';
    };
    apply();

    try {
      localStorage.setItem(STORAGE_KEY, themePref);
    } catch {
      // ignore storage failures
    }

    if (themePref !== 'system' || typeof matchMedia === 'undefined') return;
    const mq = matchMedia('(prefers-color-scheme: dark)');
    mq.addEventListener('change', apply);
    return () => {
      mq.removeEventListener('change', apply);
    };
  }, [themePref]);
}
